/**
 * Entrenar — DESKTOP.
 *
 * Otra forma, no la misma estirada. Acá **todos los ejercicios están editables a la vez** en una
 * tabla: se ve la sesión completa de una sola mirada y se registra con el teclado (escribir el
 * peso, Tab, las reps, Enter). En el celular eso sería imposible —los campos serían de 40 px y
 * no hay teclado físico—, y por eso ahí hay una hoja de a un ejercicio.
 *
 * Lo que no cambia: la lógica es la misma de `useRegistro`, y cada serie se guarda al momento.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { fechaLarga, formatearDuracion, formatearNumero, type Hallazgo, type MotivoOmision } from '@mg/core';

import {
  MOTIVOS,
  motivoHumano,
  useRegistro,
  useTemporizador,
  type EjercicioEnSesion,
  type ResultadoDelMas,
} from '../features/useRegistro.ts';
import { Aviso, Icono, Insignia, Pila, Selector, Vacio } from '../ui/componentes.tsx';
import { Pista } from '../ui/Pista.tsx';
import { useRuta } from '../ui/router.tsx';

export function RegistroDesktop(): ReactNode {
  const r = useRegistro();
  const t = useTemporizador();
  const { ir } = useRuta();
  const [errores, setErrores] = useState<Record<string, Hallazgo[]>>({});
  const banda = useRef<HTMLDivElement>(null);

  // Al cerrar, el resumen tiene que quedar a la vista: con una tabla larga, cerrar desde la barra
  // lateral no cambiaba nada de lo que el usuario estaba mirando.
  useEffect(() => {
    if (r.cerrada) banda.current?.scrollIntoView({ block: 'start' });
  }, [r.cerrada]);

  if (r.datos.cargando) return <Vacio>Cargando…</Vacio>;

  if (!r.sesion) {
    return (
      <div className="d-rejilla d-rejilla--2">
        <section className="tarjeta">
          <span className="eyebrow">Según la rotación, hoy toca</span>
          <h2 className="titulo" style={{ marginTop: 4, fontSize: 24 }}>
            {r.diaSugerido?.nombre ?? 'Sin rutina configurada'}
          </h2>

          {r.diaSugerido && (
            <table className="tabla" style={{ marginTop: 'var(--e4)' }}>
              <thead>
                <tr>
                  <th>Ejercicio</th>
                  <th>Ubicación</th>
                  <th className="num">Última vez</th>
                </tr>
              </thead>
              <tbody>
                {(r.datos.ejerciciosPorDia.get(r.diaSugerido.id) ?? []).map((e) => {
                  const m = r.datos.maquinaDe(e.id);
                  const ult = r.datos.contextos
                    .filter((c) => c.serie.ejercicio_id === e.id && c.serie.completada)
                    .sort((a, b) => (a.fecha < b.fecha ? 1 : -1))[0];
                  return (
                    <tr key={e.id}>
                      <td>{e.nombre}</td>
                      <td className="apagado">{m?.etiqueta_ubicacion ?? 'sin registrar'}</td>
                      <td className="num">
                        {ult ? (
                          <>
                            {formatearNumero(ult.serie.peso)} {ult.serie.unidad} × {ult.serie.reps}
                          </>
                        ) : (
                          <span className="apagado">nunca</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Avisar ANTES de abrir. Abrir una sesión vacía es legítimo —para hacer sólo cardio—
              pero llegar a "0 / 0 series" sin haberlo elegido es lo que confunde. */}
          {r.diaSugerido && r.conteoDiaSugerido.resueltos === 0 && (
            <div style={{ marginTop: 'var(--e4)' }}>
              <Aviso tono="atencion" titulo="Este día no tiene ejercicios">
                {r.conteoDiaSugerido.enRutina > 0
                  ? `Tu rutina tiene ${r.conteoDiaSugerido.enRutina} para este día, pero no están en el catálogo de este gimnasio.`
                  : 'No hay ninguno cargado para este día.'}{' '}
                Podés abrir la sesión igual y elegirlos ahí, o arreglar la rutina.
              </Aviso>
            </div>
          )}

          {r.error && (
            <div style={{ marginTop: 'var(--e4)' }}>
              <Aviso tono="importante">{r.error}</Aviso>
            </div>
          )}

          <div className="fila" style={{ gap: 'var(--e3)', marginTop: 'var(--e4)' }}>
            <button
              type="button"
              className="boton boton--principal"
              onClick={() => void r.abrir()}
              disabled={r.abriendo || !r.diaSugerido}
            >
              {r.abriendo ? 'Abriendo…' : 'Empezar la sesión'}
            </button>
            <button type="button" className="boton boton--fantasma" onClick={() => ir('ajustes', 'rutina')}>
              {r.diaSugerido ? 'Editar la rutina' : 'Cargar mi rutina'}
            </button>
          </div>

          {!r.diaSugerido && (
            <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
              Falta tu rutina: sin un día cargado no hay ejercicios que abrir.
            </p>
          )}
        </section>

        {r.datos.rutinaDias.length > 1 && (
          <section className="tarjeta">
            <span className="eyebrow">O empezar otro día</span>
            <div className="columna" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
              {r.datos.rutinaDias
                .filter((d) => d.id !== r.diaSugerido?.id)
                .map((d) => (
                  <button key={d.id} type="button" className="boton boton--fantasma" onClick={() => void r.abrir(d.id)}>
                    {d.nombre}
                  </button>
                ))}
            </div>
            <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
              La rotación la definís vos. La app registra y avisa; no prescribe rutinas.
            </p>
          </section>
        )}
      </div>
    );
  }

  const cerrada = r.sesion.estado === 'completada';

  return (
    <div className="d-rejilla" style={{ gridTemplateColumns: 'minmax(0, 1fr) 320px', alignItems: 'start' }}>
      <section className="tarjeta" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="fila fila--entre" style={{ padding: 'var(--e4)' }}>
          <div>
            <span className="eyebrow">
              {cerrada ? 'Sesión cerrada' : 'Sesión en curso'} · {fechaLarga(r.sesion.fecha)}
            </span>
            <p className="lectura" style={{ fontSize: 15 }}>
              {r.totalCompletadas} / {r.totalPlanificadas} series
            </p>
            {r.omitidos.length > 0 && (
              <p className="nota--fina">
                {r.omitidos.length} {r.omitidos.length === 1 ? 'ejercicio omitido' : 'ejercicios omitidos'} (
                {r.omitidos.map((o) => o.motivo).join(' · ')})
              </p>
            )}
          </div>
          {!cerrada && (
            <span className="nota--fina">
              Escribí el peso, Tab, las reps, Enter. El ＋ registra lo que dice adentro.
            </span>
          )}
        </div>

        {/* El cierre de verdad: qué hiciste, qué quedó afuera y por qué, qué sigue y cómo salir. */}
        {cerrada && r.resumen && (
          <div ref={banda} style={{ padding: '0 var(--e4) var(--e4)' }}>
            <div className="tarjeta" style={{ borderColor: 'var(--agua)' }}>
              <div className="fila fila--entre" style={{ gap: 'var(--e4)' }}>
                <div>
                  <span className="eyebrow">Sesión cerrada</span>
                  <p className="nota" style={{ marginTop: 4 }}>
                    <span className="lectura">{r.resumen.seriesCompletadas}</span> series ·{' '}
                    <span className="lectura">{formatearNumero(r.resumen.volumenKg, 0)} kg</span>{' '}
                    levantados
                    {r.resumen.omitidos.length > 0 && (
                      <>
                        {' · '}
                        <span className="lectura">{r.resumen.omitidos.length}</span>{' '}
                        {r.resumen.omitidos.length === 1 ? 'ejercicio omitido' : 'ejercicios omitidos'} (
                        {r.resumen.omitidos.map((o) => `${o.nombre}: ${o.motivo}`).join(' · ')})
                      </>
                    )}
                  </p>
                  {r.resumen.proxima && (
                    <p className="nota--fina" style={{ marginTop: 2 }}>
                      La próxima:{' '}
                      {r.resumen.proxima.fecha ? `${fechaLarga(r.resumen.proxima.fecha)} · ` : ''}
                      {r.resumen.proxima.nombre}
                    </p>
                  )}
                </div>
                <div className="fila" style={{ gap: 'var(--e2)', flex: 'none' }}>
                  <button type="button" className="boton boton--chico" onClick={() => void r.reabrir()}>
                    Reabrir la sesión
                  </button>
                  <button
                    type="button"
                    className="boton boton--chico boton--principal"
                    onClick={() => ir('inicio')}
                  >
                    Volver al inicio
                  </button>
                </div>
              </div>
              <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
                Reabrila si te acordaste de una serie. Para corregir un peso ya registrado, el lugar
                es el historial.
              </p>
            </div>
          </div>
        )}

        {/* La sesión en curso no se cierra sola al terminar el día: puede ser de anteayer. */}
        {!cerrada && !r.esDeHoy && (
          <div style={{ padding: '0 var(--e4) var(--e4)' }}>
            <Aviso tono="atencion" titulo="Esta sesión no es de hoy">
              Quedó abierta del {fechaLarga(r.sesion.fecha)}. Podés seguir anotando ahí, o cerrarla y
              empezar la de hoy.
              <div style={{ marginTop: 'var(--e3)' }}>
                <button
                  type="button"
                  className="boton boton--chico"
                  onClick={() => void r.cerrarYEmpezarHoy()}
                  disabled={r.abriendo}
                >
                  {r.abriendo
                    ? 'Abriendo…'
                    : `Cerrar la del ${fechaLarga(r.sesion.fecha)} y empezar la de hoy`}
                </button>
              </div>
            </Aviso>
          </div>
        )}

        {/* La sesión vacía tiene salida. Antes acá se renderizaba una tabla con encabezados y cero
            filas, y el único botón vivo de la pantalla era el del cardio. */}
        {r.sesionVacia && (
          <div style={{ padding: '0 var(--e4) var(--e4)' }}>
            <Aviso tono="atencion" titulo="Esta sesión no tiene ejercicios">
              {r.sesionVacia}
              <div style={{ marginTop: 'var(--e3)' }}>
                <button
                  type="button"
                  className="boton boton--chico"
                  onClick={() => ir('ajustes', 'rutina')}
                >
                  Arreglar la rutina
                </button>
              </div>
            </Aviso>
          </div>
        )}

        {/* Sin ejercicios no se dibuja la tabla: un encabezado con cero filas se lee como algo que
            no terminó de cargar. El aviso de arriba ya dice qué pasa y qué hacer. */}
        <div className="desborda-x" hidden={r.ejercicios.length === 0}>
          <table className="tabla">
            <thead>
              <tr>
                <th style={{ minWidth: 180 }}>Ejercicio</th>
                <th>Ubicación</th>
                <th className="num" style={{ width: 108 }}>
                  Peso
                </th>
                <th className="num" style={{ width: 92 }}>
                  Reps
                </th>
                <th style={{ width: 190 }} />
                <th>Series de hoy</th>
                <th style={{ width: 150 }}>Omitir o quitar</th>
              </tr>
            </thead>
            <tbody>
              {r.ejercicios.map((item) => (
                <FilaEjercicio
                  key={item.ejercicio.id}
                  item={item}
                  errores={errores[item.ejercicio.id] ?? []}
                  cerrada={cerrada}
                  onRegistrar={async (v) => {
                    const res = await r.registrar(item, v);
                    setErrores((prev) => ({
                      ...prev,
                      [item.ejercicio.id]: res.ok ? [] : res.hallazgos.filter((h) => h.severidad === 'error'),
                    }));
                    if (res.ok) t.arrancar();
                  }}
                  onRepetir={async () => {
                    const res = await r.repetir(item);
                    if (res.tipo === 'registrada') {
                      setErrores((prev) => ({
                        ...prev,
                        [item.ejercicio.id]: res.resultado.ok
                          ? []
                          : res.resultado.hallazgos.filter((h) => h.severidad === 'error'),
                      }));
                      if (res.resultado.ok) t.arrancar();
                    }
                    return res;
                  }}
                  onOmitir={(m) => void r.omitir(item, m)}
                  onQuitar={() => void r.quitar(item)}
                  onProgreso={() => ir('progreso', item.ejercicio.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <aside className="columna" style={{ gap: 'var(--e4)' }}>
        {/* El descanso es de una sesión en curso: en una cerrada no hay nada entre qué descansar. */}
        {!cerrada && (
          <section className="tarjeta">
            <div className="fila fila--entre">
              <div>
                <span className="eyebrow">Descanso</span>
                <span className="cifra-grande">{t.corriendo ? formatearDuracion(t.segundos) : '—'}</span>
              </div>
              <button
                type="button"
                className="boton boton--chico"
                onClick={t.corriendo ? t.parar : t.arrancar}
              >
                {t.corriendo ? 'Parar' : 'Arrancar'}
              </button>
            </div>
            <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
              Arranca solo al registrar una serie. Cuenta hacia arriba: no hay un descanso correcto
              que la app deba imponerte.
            </p>
          </section>
        )}

        {!cerrada && (
          <Pista clave="teclado-registro">
            Escribí el peso, Tab, las reps, Enter. Las flechas ↑ ↓ en el campo de peso suben y bajan de
            a un escalón real de esa máquina. El ＋ registra lo que dice adentro, igual que la serie
            anterior.
          </Pista>
        )}

        {r.reorden?.cambia && !cerrada && (
          <Aviso tono="atencion" titulo="Músculo repetido de ayer">
            {r.reorden.motivo}
          </Aviso>
        )}

        {/* Sólo con la sesión abierta: sugerirle algo a una sesión ya cerrada no significa nada, y
            además la sugerencia se calcula excluyendo la propia sesión, así que en una cerrada
            todos los ejercicios saldrían como "primera vez". */}
        {!cerrada && (
          <section className="tarjeta">
            <span className="eyebrow">Sugerencias de hoy</span>
            <div className="columna" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
              {r.ejercicios
                .filter((e) => e.sugerencia.tipo !== 'mantener')
                .map((e) => (
                  <div key={e.ejercicio.id}>
                    <div className="fila" style={{ gap: 6 }}>
                      <strong style={{ fontSize: 14 }}>{e.ejercicio.nombre}</strong>
                      <Insignia tono={e.sugerencia.tipo === 'cambiar_variable' ? 'ambar' : 'agua'}>
                        {e.sugerencia.tipo === 'subir'
                          ? 'subir'
                          : e.sugerencia.tipo === 'cambiar_variable'
                            ? 'cambiar variable'
                            : 'primera vez'}
                      </Insignia>
                    </div>
                    {/* Sólo la primera frase: el motivo completo se lee en la fila donde se
                        registra, no repetido cinco veces en la barra lateral. */}
                    <p className="nota--fina">{`${e.sugerencia.motivo.split('. ')[0]}.`}</p>
                  </div>
                ))}
              {r.ejercicios.every((e) => e.sugerencia.tipo === 'mantener') && (
                <p className="nota--fina">Mantené los pesos y cerrá el rango de reps.</p>
              )}
            </div>
          </section>
        )}

        {!cerrada && (
          <section className="tarjeta">
            <span className="eyebrow">Agregar un ejercicio</span>
            <p className="nota--fina" style={{ marginTop: 4 }}>
              Lo que hagas hoy no tiene por qué ser lo que estaba planificado. Entra con 3 series
              pendientes, y se saca con "Quitar" si te equivocaste.
            </p>
            <div style={{ marginTop: 'var(--e3)' }}>
              <Selector
                etiqueta=""
                value=""
                onChange={(e) => {
                  if (e.target.value) void r.agregar(e.target.value);
                }}
              >
                <option value="">
                  {r.agregables.length === 0 ? 'Ya están todos en la sesión' : 'Elegí uno…'}
                </option>
                {r.agregables.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nombre}
                    {r.datos.maquinaDe(e.id) ? ` · ${r.datos.maquinaDe(e.id)!.etiqueta_ubicacion}` : ''}
                  </option>
                ))}
              </Selector>
            </div>
          </section>
        )}

        <section className="tarjeta">
          <span className="eyebrow">Cardio</span>
          {r.cardioDeLaSesion ? (
            <div style={{ marginTop: 'var(--e2)' }}>
              <span className="lectura">
                {formatearDuracion(r.cardioDeLaSesion.duracion_seg)} ·{' '}
                {formatearNumero(r.cardioDeLaSesion.distancia_km, 2)} km ·{' '}
                {formatearNumero(r.cardioDeLaSesion.calorias, 0)} cal
              </span>
              {r.cardioDeLaSesion.nivel_resistencia === null && (
                <p className="nota--fina" style={{ color: 'var(--ambar)', marginTop: 4 }}>
                  Sin nivel de resistencia: esta sesión no se puede comparar con las otras.
                </p>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="boton boton--chico"
              style={{ marginTop: 'var(--e3)' }}
              onClick={() => ir('ingesta')}
            >
              <Icono nombre="camara" tamano={15} />
              Cargar con una foto
            </button>
          )}
        </section>

        {!cerrada && (
          <div className="columna" style={{ gap: 'var(--e2)' }}>
            <button type="button" className="boton" onClick={() => void r.cerrar()}>
              Cerrar la sesión
            </button>
            {r.seriesPendientes > 0 && (
              <p className="nota--fina">
                Se descartan {r.seriesPendientes}{' '}
                {r.seriesPendientes === 1 ? 'serie no hecha' : 'series no hechas'}. Si a alguna le
                falta un motivo, marcalo en la columna "Omitir" antes de cerrar.
              </p>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function FilaEjercicio({
  item,
  errores,
  cerrada,
  onRegistrar,
  onRepetir,
  onOmitir,
  onQuitar,
  onProgreso,
}: {
  item: EjercicioEnSesion;
  errores: Hallazgo[];
  cerrada: boolean;
  onRegistrar: (v: { peso: number; reps: number; unidad: typeof item.unidad }) => Promise<void>;
  onRepetir: () => Promise<ResultadoDelMas>;
  onOmitir: (m: MotivoOmision) => void;
  onQuitar: () => void;
  onProgreso: () => void;
}): ReactNode {
  const marca = item.sugerencia.ultimaVez;
  // Como texto, para poder estar VACÍO: precargado en 0, "Registrar 0 lb × 10" se guardaba sin
  // que nada se quejara, y 0 es un peso válido para el validador.
  const [peso, setPeso] = useState<string>(
    item.proxima.peso === null ? '' : formatearNumero(item.proxima.peso, 2),
  );
  const [reps, setReps] = useState<number>(item.proxima.reps);
  const [confirmandoQuitar, setConfirmandoQuitar] = useState(false);
  const refPeso = useRef<HTMLInputElement>(null);
  const refReps = useRef<HTMLInputElement>(null);

  // Cuando cambia la sugerencia (porque se registró una serie), los campos siguen el nuevo valor.
  useEffect(() => {
    const ult = item.completadas[item.completadas.length - 1];
    if (ult) {
      setPeso(formatearNumero(ult.peso, 2));
      setReps(ult.reps);
    }
  }, [item.completadas.length]);

  const pesoNum = Number(peso.replace(',', '.'));
  const pesoListo = peso.trim() !== '' && Number.isFinite(pesoNum) && pesoNum > 0;
  const activo = !cerrada && item.omitida === null;

  const enviar = () => {
    if (!pesoListo) {
      refPeso.current?.focus();
      return;
    }
    void onRegistrar({ peso: pesoNum, reps, unidad: item.unidad });
  };

  const enfocarPeso = () => {
    refPeso.current?.focus();
    refPeso.current?.select();
  };

  return (
    <>
      <tr style={{ opacity: item.omitida ? 0.5 : 1 }}>
        <td>
          <div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
            <strong>{item.ejercicio.nombre}</strong>
            {item.arrastradoDe && <Insignia tono="ambar">arrastrado</Insignia>}
            {item.estancado && <Insignia tono="ambar">estancado</Insignia>}
            {item.omitida && <Insignia tono="alerta">{motivoHumano(item.omitida.motivo_omision)}</Insignia>}
          </div>
          {marca && (
            <span className="nota--fina lectura">
              últ. {formatearNumero(marca.peso)} {marca.unidad} × {marca.reps.join('/')} · {marca.fecha}
            </span>
          )}
          <div>
            <button
              type="button"
              className="boton boton--chico boton--fantasma"
              style={{ minHeight: 26, padding: '0 6px', fontSize: 12, fontWeight: 500 }}
              onClick={onProgreso}
            >
              Ver su progresión
            </button>
          </div>
        </td>

        <td className="apagado" style={{ fontSize: 13 }}>
          {item.maquina?.etiqueta_ubicacion ?? <span className="nota--fina">sin registrar</span>}
        </td>

        <td>
          <input
            ref={refPeso}
            className="campo"
            style={{ minHeight: 36, textAlign: 'right' }}
            inputMode="decimal"
            disabled={!activo}
            value={peso}
            placeholder={item.unidad}
            onChange={(e) => setPeso(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                refReps.current?.focus();
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setPeso(formatearNumero((Number.isFinite(pesoNum) ? pesoNum : 0) + item.incremento, 2));
              }
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setPeso(
                  formatearNumero(Math.max(0, (Number.isFinite(pesoNum) ? pesoNum : 0) - item.incremento), 2),
                );
              }
            }}
            aria-label={`Peso de ${item.ejercicio.nombre} en ${item.unidad}`}
          />
        </td>

        <td>
          <input
            ref={refReps}
            className="campo"
            style={{ minHeight: 36, textAlign: 'right' }}
            inputMode="numeric"
            disabled={!activo}
            value={reps}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isInteger(n)) setReps(n);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                enviar();
              }
            }}
            aria-label={`Reps de ${item.ejercicio.nombre}`}
          />
        </td>

        <td>
          {/* Con la sesión cerrada los botones desaparecen: quedaban con aspecto de activos y
              registrar en una sesión cerrada no es una operación que exista. */}
          {cerrada ? (
            <span className="nota--fina">sesión cerrada</span>
          ) : item.omitida ? (
            <span className="nota--fina">omitido · {motivoHumano(item.omitida.motivo_omision)}</span>
          ) : (
            <div className="fila" style={{ gap: 4 }}>
              <button type="button" className="boton boton--chico" onClick={enviar} disabled={!pesoListo}>
                Registrar
              </button>
              <button
                type="button"
                className="boton boton--chico boton--principal"
                onClick={async () => {
                  const res = await onRepetir();
                  // El ＋ nunca se queda mudo: sin serie anterior no hay de qué copiar, así que
                  // manda el foco al peso, que es lo único que falta.
                  if (res.tipo === 'sin_referencia') enfocarPeso();
                }}
                aria-label={`Registrar una serie de ${item.ejercicio.nombre} igual a la anterior`}
                title={
                  item.proxima.peso === null
                    ? 'No hay una serie anterior de la que copiar: poné el peso'
                    : `Registrar ${formatearNumero(item.proxima.peso)} ${item.proxima.unidad} × ${item.proxima.reps}, igual que la anterior`
                }
                style={{ padding: '0 8px' }}
              >
                <Icono nombre="mas" tamano={15} />
                {item.proxima.peso !== null && (
                  <span className="lectura" style={{ fontSize: 12 }}>
                    {formatearNumero(item.proxima.peso)} {item.proxima.unidad} × {item.proxima.reps}
                  </span>
                )}
              </button>
            </div>
          )}
        </td>

        <td>
          <Pila
            estados={Array.from({ length: Math.max(item.seriesObjetivo, item.completadas.length) }, (_, i) =>
              i < item.completadas.length ? 'llena' : 'hueca',
            )}
            alto={10}
          />
          {item.completadas.length > 0 && (
            <span className="nota--fina lectura">
              {item.completadas.map((s) => `${formatearNumero(s.peso)}×${s.reps}`).join('  ')}
            </span>
          )}
        </td>

        <td>
          <select
            className="campo campo--texto"
            style={{ minHeight: 34, fontSize: 12 }}
            disabled={!activo}
            value=""
            onChange={(e) => {
              if (e.target.value) onOmitir(e.target.value as MotivoOmision);
            }}
            aria-label={`Marcar ${item.ejercicio.nombre} como omitido`}
          >
            <option value="">—</option>
            {MOTIVOS.map((m) => (
              <option key={m.valor} value={m.valor}>
                {m.etiqueta}
              </option>
            ))}
          </select>

          {/* Quitar es otra cosa que omitir: omitido queda registrado con su motivo, quitado no
              existió. Sólo mientras no tenga ninguna serie hecha. */}
          {activo &&
            item.completadas.length === 0 &&
            (confirmandoQuitar ? (
              <div className="fila" style={{ gap: 4, marginTop: 4 }}>
                <button type="button" className="boton boton--chico" onClick={() => setConfirmandoQuitar(false)}>
                  No
                </button>
                <button type="button" className="boton boton--chico boton--peligro" onClick={onQuitar}>
                  Sí, quitar
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="boton boton--chico boton--fantasma"
                style={{ marginTop: 4 }}
                onClick={() => setConfirmandoQuitar(true)}
              >
                Quitar
              </button>
            ))}
        </td>
      </tr>

      {(errores.length > 0 || (activo && !pesoListo)) && (
        <tr>
          <td colSpan={7} style={{ paddingTop: 0 }}>
            {errores.length > 0 ? (
              <Aviso tono="importante">{errores.map((h) => h.mensaje).join(' ')}</Aviso>
            ) : (
              <p className="nota--fina">
                {item.ejercicio.nombre}: poné el peso que usaste — la próxima vez viene solo.
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
